import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4p4e67iu80n4l8qnml0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-teal-600 text-sm font-semibold mb-2 tracking-wider">CONTACT</p>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">お問い合わせ・アクセス</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            ご質問やご相談など、お気軽にお問い合わせください。
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div>
            <div className="bg-gray-50 p-8 rounded-lg mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">お電話でのお問い合わせ</h3>
              <a href="tel:025-381-2476" className="block mb-6 cursor-pointer">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-teal-600 rounded-full flex items-center justify-center">
                    <i className="ri-phone-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-teal-600">025-381-2476</p>
                    <p className="text-sm text-gray-600">受付時間:10:00 ~ 17:00</p>
                  </div>
                </div>
              </a>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">会社情報</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-building-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <p className="font-semibold text-gray-900">株式会社南場組</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-map-pin-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <p className="text-gray-700">〒950-0168</p>
                    <p className="text-gray-700">新潟市江南区早通4丁目1番21号</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-time-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <p className="text-gray-700">営業時間：平日 8:00 ~ 17:00</p>
                    <p className="text-gray-700">定休日：土曜・日曜・祝日</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <form onSubmit={handleSubmit} className="bg-gray-50 p-8 rounded-lg" data-readdy-form id="contact-form">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                    会社名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                    placeholder="株式会社〇〇"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                    placeholder="025-123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-2">
                    お問い合わせ種別 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="求人・募集">求人・募集</option>
                    <option value="工事に関するご相談">工事に関するご相談</option>
                    <option value="その他のお問い合わせ">その他のお問い合わせ</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-600 focus:border-transparent resize-none"
                    placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-600 text-white font-semibold py-4 rounded-md hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
                    お問い合わせを受け付けました。ありがとうございます。
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
                    送信に失敗しました。もう一度お試しください。
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="bg-gray-50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">アクセスマップ</h3>
          <div className="aspect-video w-full rounded-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3173.8!2d139.1!3d37.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5ff4c7b5e5e5e5e5%3A0x5e5e5e5e5e5e5e5e!2z5paw5r2f5biC5rGf5Y2X5Yy65pep6YCa77yU5LiB55uu77yR4oiS77yS77yR!5e0!3m2!1sja!2sjp!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="株式会社南場組 所在地"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
}
